
import React from 'react';
import { Helmet } from 'react-helmet-async';
import Layout from '@/components/Layout/Layout';
import { Button } from '@/components/ui/button';
import { Phone, Calendar, Star, Shield, Clock, Trophy, MapPin } from 'lucide-react';

const BestRooferNearMeGreenvilleNC: React.FC = () => {
  return (
    <Layout>
      <Helmet>
        <title>Best Roofer Near Me in Greenville NC - Carolina Trophy Roofs</title>
        <meta name="description" content="Looking for the best roofer near you in Greenville, NC? Carolina Trophy Roofs is your trusted local roofing contractor with expert residential and commercial roofing services." />
        <meta name="keywords" content="best roofer Greenville NC, roofing contractor near me, Greenville roofing services, Carolina Trophy Roofs" />
        <link rel="canonical" href="https://carolinatrophyroofs.com/best-roofer-near-me-in-greenville-nc-carolina-trophy-roofs" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-brand-charcoal-gray to-gray-700 text-white py-20 px-6">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Best Roofer Near Me in <span className="text-brand-trophy-gold">Greenville, NC</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-4xl mx-auto">
              Carolina Trophy Roofs is Greenville's most trusted roofing contractor, serving Pitt County with expert residential and commercial roofing services since 2015.
            </p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => window.open('tel:+12523712266', '_self')}
                className="bg-brand-trophy-gold text-black hover:bg-brand-trophy-gold/90 font-bold text-lg px-8 py-4"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call Now: (252) 371-2266
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => window.open('https://carolinatrophyroofs.sky-quote.com/schedule#', '_blank')}
                className="border-2 border-brand-trophy-gold text-brand-trophy-gold hover:bg-brand-trophy-gold hover:text-black font-bold text-lg px-8 py-4"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Free Inspection
              </Button>
            </div>
          </div>
        </section>

        {/* Why We're The Best Section */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-brand-charcoal-gray">
              Why Carolina Trophy Roofs is the Best Choice in Greenville, NC
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-brand-trophy-gold rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="h-8 w-8 text-black" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-brand-charcoal-gray">Local Expertise</h3>
                <p className="text-gray-600">
                  Born and raised in Eastern NC, we understand Greenville's unique climate challenges and building codes. Our team has completed over 2,000 roofing projects in Pitt County.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-brand-trophy-gold rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-black" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-brand-charcoal-gray">24/7 Emergency Service</h3>
                <p className="text-gray-600">
                  Storm damage doesn't wait for business hours. We provide emergency roofing services 24/7 to protect your Greenville home or business from further damage.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-brand-trophy-gold rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trophy className="h-8 w-8 text-black" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-brand-charcoal-gray">Award-Winning Quality</h3>
                <p className="text-gray-600">
                  Winner of the 2023 Greenville Business Excellence Award for roofing contractors. We maintain A+ BBB rating and 5-star customer reviews.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Office Location Section */}
        <section className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-brand-charcoal-gray">
              <MapPin className="inline-block mr-3 h-10 w-10 text-brand-trophy-gold" />
              Our Greenville Office Locations
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-brand-charcoal-gray mb-2">Carolina Trophy Roofs - Greenville</h3>
                  <p className="text-lg text-gray-600">1414 Forbes St, Greenville, NC 27858</p>
                  <p className="text-lg text-brand-trophy-gold font-semibold">(252) 371-2266</p>
                </div>
                <div className="flex justify-center">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3244.087120143373!2d-77.37505999999999!3d35.6009175!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89aee7fe8cc00001%3A0xc20c0c573e2a4576!2s1414%20Forbes%20St%2C%20Greenville%2C%20NC%2027858!5e0!3m2!1sen!2sus!4v1753327512110!5m2!1sen!2sus"
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="rounded-lg shadow-md max-w-2xl"
                    title="Carolina Trophy Roofs Greenville Office Location"
                  />
                </div>
                <div className="mt-6 text-center">
                  <p className="text-gray-600 mb-4">
                    Conveniently located in the heart of Greenville, serving all of Pitt County and surrounding areas.
                  </p>
                  <div className="flex flex-col md:flex-row gap-4 justify-center">
                    <Button 
                      onClick={() => window.open('https://www.google.com/maps/search/?api=1&query=1414+Forbes+St,+Greenville,+NC+27858', '_blank')}
                      className="bg-brand-trophy-gold text-black hover:bg-brand-trophy-gold/90"
                    >
                      <MapPin className="mr-2 h-4 w-4" />
                      Get Directions
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => window.open('tel:+12523712266', '_self')}
                      className="border-brand-trophy-gold text-brand-trophy-gold hover:bg-brand-trophy-gold hover:text-black"
                    >
                      <Phone className="mr-2 h-4 w-4" />
                      Call Now
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-brand-charcoal-gray">
              Complete Roofing Services in Greenville, NC
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-brand-trophy-gold">
                <h3 className="text-xl font-bold mb-3 text-brand-charcoal-gray">Residential Roofing</h3>
                <p className="text-gray-600 mb-4">
                  Complete roof replacement, repair, and maintenance for Greenville homes. We work with all roofing materials including asphalt shingles, metal, and tile.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Roof Replacement</li>
                  <li>• Roof Repair</li>
                  <li>• Gutter Installation</li>
                  <li>• Siding Services</li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-brand-trophy-gold">
                <h3 className="text-xl font-bold mb-3 text-brand-charcoal-gray">Commercial Roofing</h3>
                <p className="text-gray-600 mb-4">
                  Professional commercial roofing services for Greenville businesses. From small offices to large industrial buildings.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Flat Roof Systems</li>
                  <li>• Metal Roofing</li>
                  <li>• Roof Coating</li>
                  <li>• Preventive Maintenance</li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-brand-trophy-gold">
                <h3 className="text-xl font-bold mb-3 text-brand-charcoal-gray">Storm Damage Repair</h3>
                <p className="text-gray-600 mb-4">
                  Expert storm damage assessment and repair services. We work directly with insurance companies to ensure your claim is handled properly.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Insurance Claims Assistance</li>
                  <li>• Emergency Tarping</li>
                  <li>• Hail Damage Repair</li>
                  <li>• Wind Damage Restoration</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-brand-charcoal-gray">
              What Greenville Customers Say
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="flex items-center mb-4">
                  <div className="flex text-brand-trophy-gold">
                    <Star className="h-5 w-5 fill-current" />
                    <Star className="h-5 w-5 fill-current" />
                    <Star className="h-5 w-5 fill-current" />
                    <Star className="h-5 w-5 fill-current" />
                    <Star className="h-5 w-5 fill-current" />
                  </div>
                  <span className="ml-2 text-gray-600">5.0 out of 5</span>
                </div>
                <p className="text-gray-600 mb-4">
                  "Carolina Trophy Roofs replaced our entire roof after hurricane damage. The team was professional, efficient, and cleaned up perfectly. Best roofer in Greenville!"
                </p>
                <div className="text-sm text-gray-500">
                  - Sarah M., Greenville Homeowner
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="flex items-center mb-4">
                  <div className="flex text-brand-trophy-gold">
                    <Star className="h-5 w-5 fill-current" />
                    <Star className="h-5 w-5 fill-current" />
                    <Star className="h-5 w-5 fill-current" />
                    <Star className="h-5 w-5 fill-current" />
                    <Star className="h-5 w-5 fill-current" />
                  </div>
                  <span className="ml-2 text-gray-600">5.0 out of 5</span>
                </div>
                <p className="text-gray-600 mb-4">
                  "Outstanding service from start to finish. They handled our commercial building's roof replacement with minimal disruption to our business operations."
                </p>
                <div className="text-sm text-gray-500">
                  - Mike R., Business Owner
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 px-6">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-brand-charcoal-gray">
              Serving Greenville and Surrounding Areas
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              We proudly serve Pitt County and surrounding communities in Eastern North Carolina
            </p>
            <div className="grid md:grid-cols-4 gap-4 text-center">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-brand-charcoal-gray">Greenville</h3>
                <p className="text-sm text-gray-600">Population: 87,521</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-brand-charcoal-gray">Winterville</h3>
                <p className="text-sm text-gray-600">15 minutes from Greenville</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-brand-charcoal-gray">Ayden</h3>
                <p className="text-sm text-gray-600">20 minutes from Greenville</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-brand-charcoal-gray">Farmville</h3>
                <p className="text-sm text-gray-600">25 minutes from Greenville</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-6 bg-brand-charcoal-gray text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Work with Greenville's Best Roofer?
            </h2>
            <p className="text-xl mb-8">
              Get your free roofing inspection and quote today. Call Carolina Trophy Roofs - your trusted local roofing contractor.
            </p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => window.open('tel:+12523712266', '_self')}
                className="bg-brand-trophy-gold text-black hover:bg-brand-trophy-gold/90 font-bold text-lg px-8 py-4"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call Now: (252) 371-2266
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => window.open('https://carolinatrophyroofs.sky-quote.com/Instant-Estimate', '_blank')}
                className="border-2 border-brand-trophy-gold text-brand-trophy-gold hover:bg-brand-trophy-gold hover:text-black font-bold text-lg px-8 py-4"
              >
                Get Free Quote Online
              </Button>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default BestRooferNearMeGreenvilleNC;
